// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// A MESMA VIAGEM EM MAIS DO QUE UM AUTOCARRO
// ============================================================================
//
// 📗 Cliente, 2026-08-08: *«valide se isto já existe na escala real em anexo; se existir tem
//    de ser implementado aqui também»*.
//
// ✅ **EXISTE, e está medido na folha real** (`docs/00-fontes/…Escala VdA…`): **41 pares em
//    36 dias distintos** têm a mesma referência de reserva em **duas viaturas no mesmo dia**
//    — uma com o itinerário completo e a outra só com a referência:
//
//      viatura  74 · «YB0102139OZ»
//      viatura 105 · «GRX/BCN (Eesa) YB0102139OZ»
//
//    É o mesmo grupo em dois autocarros, e repete-se com os mesmos clientes (Eesa, Mercury).
//
// ⚠️ **A PRIMEIRA MEDIÇÃO QUE FIZ ESTAVA INFLACIONADA, e vale a pena dizer porquê.** O meu
//    padrão truncava as referências: `VGT251226OZ` e `VGT251227TWB` colapsavam no mesmo
//    prefixo `VGT25122` e davam «cinco viaturas na mesma viagem». Eram **cinco grupos
//    diferentes do mesmo cliente**. Refeito com a assinatura inteira: 68 → 41.
//
// 📌 **O MODELO JÁ SUPORTA ISTO — o que faltava era o ecrã DIZÊ-LO.** Duas alocações com a
//    mesma `referencia`, no mesmo dia, em viaturas diferentes: é o que a base já guarda, e é
//    o que o gerador já produz (medido no `dev`: **5 721 pares dia×referência com mais do
//    que uma viatura**, e **1 110 de 11 079 referências**, 10 %).
//
// ⚠️ **Sem isto, o mapa desenha duas barras iguais e nada diz que são a mesma viagem.** Quem
//    move uma sem saber da outra parte um grupo ao meio — e a operação só descobre no dia.
//
// 📌 **É lógica pura sobre a grelha que o mapa já tem**, como `substituicao.ts`: não precisa
//    de rota nova nem de consulta à base.
// ============================================================================
/**
 * Que outras viaturas levam a **mesma corrida** que a célula `(viatura, dia)`.
 *
 * Devolve vazio quando a célula não tem corrida — um dia parado ou uma reserva sem
 * identidade não se pode comparar com nada.
 *
 * ⚠️ **A chave é a CORRIDA e não a rota** (F-19): 2 679 assinaturas de rota servem 15 132
 * corridas, e é a corrida que liga a mesma viagem em duas viaturas.
 */
export function outrasViaturasDaViagem(dados, viatura, dia) {
    const c = dados.grelha[viatura]?.[dia] ?? null;
    if (c == null)
        return [];
    const corrida = c[10];
    if (corrida < 0)
        return [];
    // Os dias que ESTA viatura faz desta corrida — para decidir se a outra se sobrepõe.
    const meus = diasDaCorrida(dados, viatura, corrida);
    if (meus === null)
        return [];
    const fora = [];
    for (let v = 0; v < dados.grelha.length; v++) {
        if (v === viatura)
            continue;
        const dela = diasDaCorrida(dados, v, corrida);
        if (dela === null)
            continue;
        fora.push({
            viatura: v,
            numero: dados.viaturas[v]?.numero ?? String(v),
            dia0: dela.dia0,
            dia1: dela.dia1,
            // Sobreposição de intervalos: começa antes de o outro acabar, e vice-versa.
            simultanea: dela.dia0 <= meus.dia1 && meus.dia0 <= dela.dia1,
        });
    }
    // A que se sobrepõe primeiro: é a que tem consequência imediata.
    return fora.sort((a, b) => Number(b.simultanea) - Number(a.simultanea) || a.dia0 - b.dia0);
}
/**
 * O primeiro e o último dia em que a viatura `v` leva a corrida `corrida`.
 *
 * ⚠️ **Varre a linha inteira e não só à volta de um dia.** Uma corrida pode ser interrompida
 * — a viatura larga o grupo, faz outra coisa, e volta a apanhá-lo — e limitar-se ao bloco
 * contíguo daria um intervalo mais curto do que o real. O que se quer é «de quando a quando
 * é que esta viatura anda nesta viagem».
 */
function diasDaCorrida(dados, v, corrida) {
    const linha = dados.grelha[v] ?? [];
    let dia0 = -1;
    let dia1 = -1;
    for (let d = 0; d < linha.length; d++) {
        if ((linha[d]?.[10] ?? -1) !== corrida)
            continue;
        if (dia0 < 0)
            dia0 = d;
        dia1 = d;
    }
    return dia0 < 0 ? null : { dia0, dia1 };
}
//# sourceMappingURL=mesma-viagem.js.map